import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import type { ListBuilderConfiguration } from '../types';

/**
 * Order status value mapping for badge display
 */
const ORDER_STATUS_MAPPING = [
	{
		value: '1',
		displayText: 'Unsubmitted',
		color: { type: 'neutral' },
	},
	{
		value: '2',
		displayText: 'Pending',
		color: { type: 'warning' },
	},
	{
		value: '3',
		displayText: 'Sent to ERP',
		color: { type: 'info' },
	},
	{
		value: '4',
		displayText: 'Approved',
		color: { type: 'success' },
	},
	{
		value: '5',
		displayText: 'Shipped',
		color: { type: 'info' },
	},
	{
		value: '6',
		displayText: 'Held',
		color: { type: 'warning' },
	},
	{
		value: '7',
		displayText: 'Cancelled',
		color: { type: 'neutral' },
	},
	{
		value: '8',
		displayText: 'Approval Required',
		color: { type: 'warning' },
	},
	{
		value: '9',
		displayText: 'Override Rejected',
		color: { type: 'danger' },
	},
	{
		value: '10',
		displayText: 'Cancellation Requested',
		color: { type: 'warning' },
	},
	{
		value: '12',
		displayText: 'Submitted',
		color: { type: 'success' },
	},
	{
		value: 'Partially Shipped',
		displayText: 'Partially Shipped',
		color: { type: 'info' },
	},
];

/**
 * Order origin value mapping for badge display
 */
// Origin is a CATEGORY: every value takes the same neutral chip (owner
// decision 2026-08-03, PD-15196 option B).
const ORDER_ORIGIN_MAPPING = [
	{ value: '1', displayText: 'CRM', color: { type: 'neutral' } },
	{ value: '2', displayText: 'ERP', color: { type: 'neutral' } },
	{
		value: '3',
		displayText: 'E-Commerce',
		color: { type: 'neutral' },
	},
];

/**
 * Orders embedded in invoice view configuration
 */
export const ORDER_INVOICE_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'invoice-embedded-orders-all',
			cacheTTL: 600000,
			name: 'All Orders',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'status-origin',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'status',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'value-mapping',
									valueMapping: [
										{ value: '1', color: { type: 'neutral' } },
										{ value: '2', color: { type: 'warning' } },
										{ value: '3', color: { type: 'info' } },
										{ value: '4', color: { type: 'success' } },
										{ value: '5', color: { type: 'info' } },
										{ value: '6', color: { type: 'warning' } },
										{ value: '7', color: { type: 'neutral' } },
										{ value: '8', color: { type: 'warning' } },
										{ value: '9', color: { type: 'danger' } },
										{ value: '10', color: { type: 'warning' } },
										{ value: '12', color: { type: 'success' } },
										{
											value: 'Partially Shipped',
											color: { type: 'info' },
										},
									],
								},
								valueMapping: ORDER_STATUS_MAPPING,
							},
							displayStyle: 'badge',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'origin-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'origin',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'value-mapping',
									valueMapping: [
										{ value: '1', color: { type: 'neutral' } },
										{ value: '2', color: { type: 'warning' } },
										{ value: '3', color: { type: 'info' } },
									],
								},
								valueMapping: ORDER_ORIGIN_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					headers: [
						{ id: 'h1-status', text: 'Status' },
						{ id: 'h1-origin', text: 'Origin' },
					],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'status', objectTable: 'order' },
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'order-erp-numbers',
					fields: [
						{
							id: 'order-id-field',
							link: {
								type: 'object',
								enabled: true,
								idField: '_id',
								objectTable: 'order',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_id',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'statusInfo' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'external-id-field',
							dataConfig: {
								type: 'conditional',
								conditions: [
									{
										field: 'externalId2',
										operator: 'not_equals',
										value: null,
										dataConfig: {
											type: 'field',
											field: {
												fieldName: 'externalId2',
												objectTable: 'order',
											},
										},
									},
								],
								defaultDataConfig: {
									type: 'field',
									field: {
										fieldName: 'externalId',
										objectTable: 'order',
									},
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h2-order', text: 'Order #' },
						{ id: 'h2-erp', text: 'ERP #' },
					],
					minWidth: 100,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'id', objectTable: 'order' },
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'dates',
					fields: [
						{
							id: 'date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'date',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'datetime-short' },
								allowTextWrap: false,
							},
							displayStyle: 'date',
						},
						{
							id: 'line-break-5',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'updated-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_modified_date',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
								formatOptions: { dateFormat: 'datetime-short' },
								allowTextWrap: false,
							},
							displayStyle: 'date',
						},
					],
					headers: [
						{ id: 'h4-date', text: 'Date' },
						{ id: 'h4-updated', text: 'Last Updated' },
					],
					minWidth: 110,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'DESC', fieldName: 'date', objectTable: 'order' },
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'reps',
					fields: [
						{
							id: 'assigned-rep-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'assignedRepName',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-6',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'submitted-by-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'submitterName',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h5-assigned', text: 'Assigned Rep' },
						{ id: 'h5-submitted', text: 'Submitted By' },
					],
					minWidth: 140,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'assignedRepName',
							objectTable: 'order',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'order-lines',
					fields: [
						{
							id: 'lines-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'numberOfLines',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h6', text: 'Lines' }],
					minWidth: 60,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'numberOfLines',
							objectTable: 'order',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'order-costs',
					fields: [
						{
							id: 'tax-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'tax',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-7',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'freight-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'freight',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-8',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'discount-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'discount',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h7-tax', text: 'Tax' },
						{ id: 'h7-freight', text: 'Freight' },
						{ id: 'h7-discount', text: 'Discount' },
					],
					minWidth: 120,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{ direction: 'DESC', fieldName: 'tax', objectTable: 'order' },
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'order-totals',
					fields: [
						{
							id: 'subtotal-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'subtotal',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-9',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'total-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'total',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h8-subtotal', text: 'Subtotal' },
						{ id: 'h8-total', text: 'Total' },
					],
					minWidth: 120,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{ direction: 'DESC', fieldName: 'total', objectTable: 'order' },
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'gp-totals',
					fields: [
						{
							id: 'total-gp-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'totalGP',
									objectTable: 'order',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
							requiredPermission: GP_VIEW_PERMISSION,
						},
					],
					headers: [{ id: 'h-total-gp', text: 'GP' }],
					minWidth: 120,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{ direction: 'DESC', fieldName: 'totalGP', objectTable: 'order' },
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
			],
			description: 'Complete list of all orders with key details',
			defaultSortId: 'column-dates-desc',
			requiredFields: [
				'_id',
				'externalId',
				'externalId2',
				'date',
				'_modified_date',
				'status',
				'origin',
				'numberOfLines',
				'assignedRepName',
				'submitterName',
				'tax',
				'freight',
				'discount',
				'discountType',
				'subtotal',
				'total',
				'totalGP',
				'companyName',
			],
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [20, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/orders/:id',
			objectLabel: 'Order',
			objectTable: 'order',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'externalId2', fieldLabel: 'External ID 2' },
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: 'clientName', fieldLabel: 'Client Name' },
				{ fieldName: 'clientAddress1', fieldLabel: 'Client Address 1' },
				{ fieldName: 'clientAddress2', fieldLabel: 'Client Address 2' },
				{ fieldName: 'clientCity', fieldLabel: 'Client City' },
				{ fieldName: 'clientState', fieldLabel: 'Client State' },
				{ fieldName: 'clientZip', fieldLabel: 'Client Zip' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'numberOfLines', fieldLabel: 'Number of Lines' },
				{ fieldName: 'total', fieldLabel: 'Total' },
				{ fieldName: 'customerPO', fieldLabel: 'Customer PO' },
			],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
			],
		},
	],
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Date (Newest First)',
			fields: [{ direction: 'DESC', fieldName: 'date', objectTable: 'order' }],
			description: 'Sort orders by date, newest first',
		},
		{
			id: 'date-asc',
			name: 'Date (Oldest First)',
			fields: [{ direction: 'ASC', fieldName: 'date', objectTable: 'order' }],
			description: 'Sort orders by date, oldest first',
		},
		{
			id: 'external-id-asc',
			name: 'Order # (Low to High)',
			fields: [{ direction: 'ASC', fieldName: 'id', objectTable: 'order' }],
			description: 'Sort orders by order ID, ascending',
		},
		{
			id: 'external-id-desc',
			name: 'Order # (High to Low)',
			fields: [{ direction: 'DESC', fieldName: 'id', objectTable: 'order' }],
			description: 'Sort orders by order ID, descending',
		},
		{
			id: 'total-desc',
			name: 'Total (Highest First)',
			fields: [{ direction: 'DESC', fieldName: 'total', objectTable: 'order' }],
			description: 'Sort orders by total amount, highest first',
		},
		{
			id: 'status',
			name: 'Status',
			fields: [{ direction: 'ASC', fieldName: 'status', objectTable: 'order' }],
			description: 'Sort orders by status',
		},
	],
};
